import { HttpClient } from '@adapters/HttpClient';
import type {
  ExportDateRangeParams,
  ExportInvoiceParams,
  ExportQueuedResponse,
  ExportStartParams,
  ExportStatusResponse,
} from '../models/export';
import { isExportDateRangeParams } from '../models/export';
import { THREE_PL_EXCEL_REPORTS, type ThreePlReportId } from '../models/reports';

function buildDateRangeQuery(params: ExportDateRangeParams): string {
  const searchParams = new URLSearchParams({
    date_from: params.date_from,
    date_to: params.date_to,
  });
  return searchParams.toString();
}

const EXPORT_API_PATHS: Record<ThreePlReportId, { start: string; status: (exportId: string) => string }> = {
  shipped_orders: {
    start: '/report/shipped-orders/export',
    status: (exportId) => `/report/shipped-orders/export/${exportId}`,
  },
  shipped_orders_sales: {
    start: '/report/shipped-orders/sales/export',
    status: (exportId) => `/report/shipped-orders/sales/export/${exportId}`,
  },
  shipped_orders_refund_payments: {
    start: '/report/shipped-orders/refund-payments/export',
    status: (exportId) => `/report/shipped-orders/refund-payments/export/${exportId}`,
  },
  invoice_audit: {
    start: '/report/invoice-audit/export',
    status: (exportId) => `/report/invoice-audit/export/${exportId}`,
  },
  active_clients: {
    start: '/report/active-clients/export',
    status: (exportId) => `/report/active-clients/export/${exportId}`,
  },
};

export class ShippedOrdersExportApiAdapter {
  constructor(private httpClient: HttpClient) {}

  async startExport(reportId: ThreePlReportId, params: ExportStartParams): Promise<ExportQueuedResponse> {
    const { start } = EXPORT_API_PATHS[reportId];
    const paramType = THREE_PL_EXCEL_REPORTS.find((report) => report.id === reportId)?.paramType;

    if (paramType === 'none') {
      return this.httpClient.post<ExportQueuedResponse>(start, {}, false);
    }

    if (isExportDateRangeParams(params)) {
      const url = `${start}?${buildDateRangeQuery(params)}`;
      return this.httpClient.post<ExportQueuedResponse>(url, {}, false);
    }

    const { invoice_id } = params as ExportInvoiceParams;
    const searchParams = new URLSearchParams({
      invoice_id: String(invoice_id),
    });
    const url = `${start}?${searchParams.toString()}`;
    return this.httpClient.post<ExportQueuedResponse>(url, {}, false);
  }

  async getExportStatus(reportId: ThreePlReportId, exportId: string): Promise<ExportStatusResponse> {
    const path = EXPORT_API_PATHS[reportId].status(exportId);
    return this.httpClient.get<ExportStatusResponse>(path, undefined, {}, false);
  }
}
